// UI helper functions for creating common UI elements

/**
 * Appends a full-width primary button inside a styled footer to a modal's bottom-sheet-content.
 * This encapsulates the repeated pattern used across all modals with a primary action.
 *
 * @param {HTMLElement} modal - The modal overlay element returned by renderModal()
 * @param {HTMLButtonElement} button - A button element (e.g. from createPrimaryButton)
 */
export function appendModalFooterButton(modal, button) {
    const buttonContainer = document.createElement('div');
    buttonContainer.style.cssText = `
        padding: 16px;
        border-top: 1px solid #eee;
        background-color: white;
    `;
    button.style.width = '100%';
    buttonContainer.appendChild(button);

    const sheetContent = modal.querySelector('.bottom-sheet-content');
    if (sheetContent) {
        sheetContent.appendChild(buttonContainer);
    }
}

/**
 * Creates a delete button for the modal header
 * @param {Function} onDelete - Callback function when delete is confirmed
 * @param {string} [confirmMessage] - Optional custom confirmation message
 * @param {Function} [preDeleteCheck] - Optional async function to run before showing confirmation. Should return {allowed: boolean, message?: string}
 * @returns {HTMLElement} The delete button element
 */
export function createDeleteButton(onDelete, confirmMessage = 'Are you sure you want to delete this expense?', preDeleteCheck = null) {
    const deleteBtn = document.createElement('button');
    deleteBtn.className = 'header-icon-btn delete-expense-btn';
    deleteBtn.innerHTML = `
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="3 6 5 6 21 6"/>
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
            <line x1="10" y1="11" x2="10" y2="17"/>
            <line x1="14" y1="11" x2="14" y2="17"/>
        </svg>
    `;
    deleteBtn.title = "Delete Expense";

    deleteBtn.onclick = async (e) => {
        e.stopPropagation();

        // Run pre-delete check if provided
        if (preDeleteCheck) {
            const checkResult = await preDeleteCheck();
            if (!checkResult.allowed) {
                if (checkResult.message) {
                    alert(checkResult.message);
                }
                return;
            }
        }

        // Show confirmation dialog
        if (confirm(confirmMessage)) {
            onDelete();
        }
    };

    return deleteBtn;
}

/**
 * Adds a delete button to a modal header
 * @param {HTMLElement} modal - The modal element
 * @param {Function} onDelete - Callback function when delete is confirmed
 * @param {string} [confirmMessage] - Optional custom confirmation message
 * @param {Function} [preDeleteCheck] - Optional async function to run before showing confirmation
 */
export function addDeleteButtonToModal(modal, onDelete, confirmMessage = undefined, preDeleteCheck = undefined) {
    const headerSection = modal.querySelector('.sheet-header-section');
    if (headerSection && !headerSection.querySelector('.delete-expense-btn')) {
        const deleteBtn = createDeleteButton(onDelete, confirmMessage, preDeleteCheck);
        headerSection.appendChild(deleteBtn);
    }
}
